import { formatCurrency, formatCurrencyNoUnit } from '@/lib/formatters';
import { Avatar, Button, styled } from '@mui/material';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';
import parse from 'html-react-parser';
import { getUser } from '@/lib/common';
import { T } from '@/theme/tokens';
import { Pill } from '@/components/ds';
import type { Tone } from '@/theme/tokens';

interface ResourceCardV2Props {
  resource: ActionCenterItemV2;
  dateFilter: number;
  setClickedButton: (resource: ResourceCardV2Props['resource']) => void;
  handleClickOpen: (
    id_dispute?: string | number,
    responsible_id?: string | number,
  ) => void;
  openDisputesDialog: (resource: ResourceCardV2Props['resource']) => void;
  openRecommendationModal?: (resource: ResourceCardV2Props['resource']) => void;
  onViewItem: (resource: ActionCenterItemV2) => void;
}
/**
 * Legacy Action Center cards keep the View action inside the dashboard modal,
 * while V2 items deep-link into the destination tab. Until the shared
 * controller takes over both code paths we keep this component focused on
 * navigation/deep-linking, leaving inline dialog handling to the legacy card.
 */
function ResourceCardV2({
  resource,
  dateFilter,
  setClickedButton,
  handleClickOpen,
  openDisputesDialog,
  openRecommendationModal,
  onViewItem,
}: Readonly<ResourceCardV2Props>) {
  const currentUserId = getUser()?.id;
  const isCurrentUserResponsible =
    currentUserId !== undefined &&
    resource.responsible_id !== null &&
    String(resource.responsible_id) === String(currentUserId);

  const title = getTitleByActionType(resource);
  const pillTone = getPillTone(resource, title);
  const type = (resource.action_type || '').toLowerCase();
  const isSavings =
    type === 'recommendation' || type === 'optimization' || type.startsWith('ai');
  const isBudget = type === 'budget' || type === 'budgets';
  const isRiskAmount =
    isBudget ||
    resource.responsible_id === null ||
    (resource.days_not_assigned_for ?? 0) > 0;

  // Second metadata cell: an "Est. saving" amount for savings items, the budget
  // amount for budget items, otherwise the item's state.
  const hasAmount = isSavings || isBudget || (resource.cost || 0) > 0;
  const cell2Label = isBudget ? 'Budget' : isSavings ? 'Est. saving' : 'Status';
  const cell2Value = hasAmount
    ? isBudget
      ? formatCurrencyNoUnit(resource.budgetValue || resource.cost || 0)
      : formatCurrency(resource.cost || 0, resource.currency)
    : resource.dispute_status || '—';
  const cell2Color = isSavings
    ? T.success
    : isRiskAmount
      ? T.danger
      : T.primary;
  const cell2IsAmount = hasAmount;

  const supervisorName =
    resource.tech_manager_name || resource.tech_owner_name || '—';
  const supInitials = getInitials(supervisorName);
  const supColor = getAvatarColor(supervisorName);
  const scope = isCurrentUserResponsible ? 'Responsible' : 'Supervised';

  return (
    <StyledCard>
      <StyledCardBody>
        <StyledTopRow>
          <Pill tone={pillTone}>{title}</Pill>
          <StyledTopRight>
            {resource.dispute_status && (
              <Pill tone={getStatePillTone(resource.dispute_status)}>
                {resource.dispute_status}
              </Pill>
            )}
            {resource.id_dispute != null && (
              <StyledDisputeId>#{resource.id_dispute}</StyledDisputeId>
            )}
          </StyledTopRight>
        </StyledTopRow>

        <StyledBody>
          {resource.name && <div>{parse(resource.name)}</div>}
        </StyledBody>

        <StyledMetaPanel>
          <StyledMetaCell>
            <StyledMetaLabel>Supervisor</StyledMetaLabel>
            <StyledSupervisor>
              <Avatar
                src={resource.tech_owner_photo || undefined}
                alt={`${supervisorName} avatar`}
                sx={{
                  width: 20,
                  height: 20,
                  fontSize: 9,
                  fontWeight: 700,
                  bgcolor: supColor,
                  color: '#fff',
                }}>
                {supInitials}
              </Avatar>
              <StyledSupervisorName>{supervisorName}</StyledSupervisorName>
            </StyledSupervisor>
          </StyledMetaCell>

          <StyledMetaCell>
            <StyledMetaLabel>{cell2Label}</StyledMetaLabel>
            <StyledMetaValue
              style={{
                color: cell2IsAmount ? cell2Color : T.textMut,
                fontWeight: cell2IsAmount ? 900 : 700,
              }}>
              {cell2Value}
            </StyledMetaValue>
          </StyledMetaCell>

          <StyledMetaCell>
            <StyledMetaLabel>Created</StyledMetaLabel>
            <StyledMetaValue>{resource.date_created_txt || '—'}</StyledMetaValue>
          </StyledMetaCell>

          <StyledMetaCell>
            <StyledMetaLabel>Scope</StyledMetaLabel>
            <StyledMetaValue>{scope}</StyledMetaValue>
          </StyledMetaCell>
        </StyledMetaPanel>
      </StyledCardBody>

      <StyledCardActions>
        <StyledPrimaryButton
          onClick={() => {
            setClickedButton(resource);
            handleClickOpen(
              resource.id_dispute ?? undefined,
              resource.responsible_id ?? undefined,
            );
          }}>
          Dispute
        </StyledPrimaryButton>

        <StyledGhostButton
          onClick={() => {
            // Any Recommendation linked to a recommendation row opens the modal
            // instead of navigating (subscription-level recs included).
            if (
              resource.action_type === 'Recommendation' &&
              openRecommendationModal &&
              resource.id_recommendation
            ) {
              openRecommendationModal(resource);
              return;
            }
            onViewItem(resource);
          }}>
          Explore
        </StyledGhostButton>
      </StyledCardActions>
    </StyledCard>
  );
}

const getTitleByActionType = (resource: any): string => {
  if (!resource?.action_type) return '';
  switch (resource.action_type.toLowerCase()) {
    case 'budget':
      return 'Budget';
    default:
      return resource.action_type;
  }
};

// Map the card's action type / title onto a design-system pill tone.
const getPillTone = (resource: ActionCenterItemV2, title: string): Tone => {
  const type = (resource.action_type || '').toLowerCase();
  if (title.toLowerCase().startsWith('ai') || type.startsWith('ai')) return 'ai';
  switch (type) {
    case 'attestation':
      return 'warn';
    case 'budget':
    case 'budgets':
      return 'danger';
    case 'allocation':
      return 'success';
    case 'recommendation':
    case 'optimization':
    default:
      return 'primary';
  }
};

// Dispute / item state -> pill tone.
const getStatePillTone = (state?: string): Tone => {
  const s = (state || '').toLowerCase();
  if (s.includes('open')) return 'success';
  if (s.includes('review')) return 'primary';
  if (s.includes('unresolved') || s.includes('pending')) return 'warn';
  if (s.includes('reject') || s.includes('overdue') || s.includes('risk')) return 'danger';
  if (s.includes('closed')) return 'neutral';
  return 'primary';
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = ['#1C71E3', '#16A55C', '#7A4FD6', '#B26A12', '#53A6F0', '#E23D3D'];
const getAvatarColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const StyledCard = styled('div')({
  flex: 'none',
  width: 290,
  minWidth: 290,
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${T.border}`,
  backgroundColor: T.surfaceAlt,
  borderRadius: 12,
  fontFamily: T.font,
  boxShadow: T.cardShadow,
  transition: 'transform .16s, box-shadow .16s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 24px rgba(16,35,58,.1)',
  },
});

const StyledCardBody = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: 15,
});

const StyledTopRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
});

const StyledTopRight = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 'none',
});

const StyledDisputeId = styled('span')({
  fontWeight: 900,
  fontSize: 12,
  color: T.textFaint2,
});

const StyledBody = styled('div')({
  fontSize: 13,
  color: T.textMut,
  lineHeight: 1.45,
  minHeight: 56,
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
});

const StyledMetaPanel = styled('div')({
  marginTop: 'auto',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '9px 12px',
  padding: 12,
  background: '#F5F8FC',
  border: '1px solid #EBF0F6',
  borderRadius: 10,
});

const StyledMetaCell = styled('div')({ minWidth: 0 });

const StyledMetaLabel = styled('div')({
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: '.4px',
  textTransform: 'uppercase',
  color: T.textFaint3,
  marginBottom: 3,
});

const StyledMetaValue = styled('div')({
  fontSize: 12,
  fontWeight: 700,
  color: T.textMut,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const StyledSupervisor = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
});

const StyledSupervisorName = styled('div')({
  fontSize: 12,
  fontWeight: 700,
  color: T.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const StyledCardActions = styled('div')({
  display: 'flex',
  gap: 8,
  padding: 15,
  paddingTop: 0,
});

const StyledActionButton = styled(Button)({
  flex: 1,
  minWidth: 0,
  textTransform: 'none',
  fontFamily: T.font,
  fontWeight: 700,
  fontSize: 12.5,
  lineHeight: 1.2,
  padding: 8,
  borderRadius: 8,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
});

const StyledPrimaryButton = styled(StyledActionButton)({
  backgroundColor: T.primary,
  color: '#fff',
  '&:hover': { backgroundColor: T.primaryHover, boxShadow: 'none' },
});

const StyledGhostButton = styled(StyledActionButton)({
  backgroundColor: T.surface,
  color: T.primary,
  border: '1px solid #D3DEEC',
  '&:hover': {
    backgroundColor: '#F2F7FD',
    border: '1px solid #9FC2F0',
    boxShadow: 'none',
  },
});

export default ResourceCardV2;
